import React from 'react';
import { ExpectedPositionData } from './types';

interface PositionSectionProps {
  selectedAmount: number | null;
  selectedOption: string | null;
  costBasis: number | null;
  currentSellValue: number | null;
  projectedWin: number | null;
  projectedMultiplier: number | null;
  hasVoted: boolean;
  expectedPositionData?: ExpectedPositionData | null;
  postVoteAmount?: number | null; // Amount being added in double-down mode
}

export const PositionSection: React.FC<PositionSectionProps> = ({
  selectedAmount,
  selectedOption,
  costBasis,
  currentSellValue,
  projectedWin,
  projectedMultiplier,
  hasVoted,
  expectedPositionData,
  postVoteAmount
}) => {

  // Calculate P/L percentage
  const getPnLPercent = (): number | null => {
    if (costBasis === null || currentSellValue === null || costBasis === 0) return null;
    return ((currentSellValue - costBasis) / costBasis) * 100;
  };

  // Build the stats bar that fills in progressively
  const getStatsBar = () => {
    // Show expected change for new vote, double down, or sell
    if (expectedPositionData) {
      if (expectedPositionData.type === 'sell') {
        // SELL: Show current position value and expected SOL return
        const solReturnText = expectedPositionData.expectedSolReturn 
          ? `${expectedPositionData.expectedSolReturn.toFixed(3)}` 
          : '—';
        
        return (
          <div className="position-stats-container">
            <div className="position-stat-item">
              <span className="position-stat-label">You'll Receive</span>
              <span className="position-stat-value">
                <img src="/assets/icons/solanaLogoMark.svg" alt="SOL" className="amount-sol-icon" />
                {solReturnText}
              </span>
            </div>
          </div>
        );
      }
      
      if (expectedPositionData.type === 'double_down') {
        // Show new stake and new projected win after adding more
        const newStakeText = expectedPositionData.newTotalDeposited 
          ? `${expectedPositionData.newTotalDeposited.toFixed(2)}` 
          : (postVoteAmount && costBasis ? `${(costBasis + postVoteAmount).toFixed(2)}` : '—');
        
        const hasNewProjection = expectedPositionData.newValue && expectedPositionData.newMultiplier;
        
        return (
          <div className="position-stats-container">
            <div className="position-stat-item">
              <span className="position-stat-label">Stake After</span>
              <span className="position-stat-value">
                <img src="/assets/icons/solanaLogoMark.svg" alt="SOL" className="amount-sol-icon" />
                {newStakeText}
              </span>
            </div>
            <div className="position-stat-item">
              <span className="position-stat-label">EST. WIN</span>
              <span className={`position-stat-value ${hasNewProjection ? 'position-highlight' : ''}`}>
                <img src="/assets/icons/solanaLogoMark.svg" alt="SOL" className="amount-sol-icon" />
                {hasNewProjection ? (
                  <>
                    {expectedPositionData.newValue!.toFixed(3)}
                    <span className="position-supplementary"> ({expectedPositionData.newMultiplier!.toFixed(1)}x)</span>
                  </>
                ) : '—'}
              </span>
            </div>
          </div>
        );
      }
      
      if (expectedPositionData.type === 'new_vote') {
        // Show stake and projected win to motivate action
        const stakeText = expectedPositionData.depositAmount 
          ? `${expectedPositionData.depositAmount.toFixed(2)}` 
          : (selectedAmount ? `${selectedAmount.toFixed(2)}` : '—');
        
        const hasProjection = expectedPositionData.expectedValue && expectedPositionData.expectedMultiplier;
        
        return (
          <div className="position-stats-container">
            <div className="position-stat-item">
              <span className="position-stat-label">Stake</span>
              <span className="position-stat-value">
                <img src="/assets/icons/solanaLogoMark.svg" alt="SOL" className="amount-sol-icon" />
                {stakeText}
              </span>
            </div>
            <div className="position-stat-item">
              <span className="position-stat-label">EST. WIN</span>
              <span className={`position-stat-value ${hasProjection ? 'position-highlight' : ''}`}>
                <img src="/assets/icons/solanaLogoMark.svg" alt="SOL" className="amount-sol-icon" />
                {hasProjection ? (
                  <>
                    {expectedPositionData.expectedValue!.toFixed(3)}
                    <span className="position-supplementary"> ({expectedPositionData.expectedMultiplier!.toFixed(1)}x)</span>
                  </>
                ) : '—'}
              </span>
            </div>
          </div>
        );
      }
    }
    
    // For users with a position, show labeled rows:
    // Current: 0.01 (+0.0%)
    // Projected: ~0.030 (3.0x)
    if (hasVoted && costBasis !== null) {
      const pnlPercent = getPnLPercent();
      const pnlClass = pnlPercent !== null 
        ? (pnlPercent >= 0 ? 'position-pnl-positive' : 'position-pnl-negative')
        : '';
      const pnlText = pnlPercent !== null 
        ? `(${pnlPercent >= 0 ? '+' : ''}${pnlPercent.toFixed(1)}%)`
        : '';
      
      return (
        <div className="position-stats-container">
          <div className="position-stat-item">
            <span className="position-stat-label">Current</span>
            <span className="position-stat-value">
              <img src="/assets/icons/solanaLogoMark.svg" alt="SOL" className="amount-sol-icon" />
              {costBasis.toFixed(2)}
              {pnlText && <span className={`position-supplementary ${pnlClass}`}> {pnlText}</span>}
            </span>
          </div>
          <div className="position-stat-item">
            <span className="position-stat-label">EST. WIN</span>
            <span className="position-stat-value">
              <img src="/assets/icons/solanaLogoMark.svg" alt="SOL" className="amount-sol-icon" />
              {projectedWin !== null && projectedMultiplier !== null ? (
                <>
                  {projectedWin.toFixed(3)}
                  <span className="position-supplementary"> ({projectedMultiplier.toFixed(1)}x)</span>
                </>
              ) : '—'}
            </span>
          </div>
        </div>
      );
    }
    
    // Default: Before voting - always show consistent two-row layout
    // This prevents UI shifting as user makes selections
    const stakeText = selectedAmount ? `${selectedAmount.toFixed(2)}` : '—';
    
    return (
      <div className="position-stats-container">
        <div className="position-stat-item">
          <span className="position-stat-label">Stake</span>
          <span className="position-stat-value">
            <img src="/assets/icons/solanaLogoMark.svg" alt="SOL" className="amount-sol-icon" />
            {stakeText}
          </span>
        </div>
        <div className="position-stat-item">
          <span className="position-stat-label">EST. WIN</span>
          <span className="position-stat-value">
            <img src="/assets/icons/solanaLogoMark.svg" alt="SOL" className="amount-sol-icon" />
            —
          </span>
        </div>
      </div>
    );
  };

  // Determine the label based on state
  const getLabel = () => {
    // Show preview labels when user is making changes
    if (expectedPositionData) {
      if (expectedPositionData.type === 'sell') {
        return 'PREVIEW SELL';
      }
      if (expectedPositionData.type === 'double_down') {
        return 'PREVIEW ADD MORE';
      }
    }
    
    // After vote is cast
    if (hasVoted) {
      return 'YOUR POSITION';
    }
    
    // Step 1: No option selected yet
    if (!selectedOption) {
      return 'Choose your side';
    }
    
    // Step 2: Option selected, but no amount
    if (!selectedAmount) {
      return 'Choose your amount';
    }
    
    // Step 3: Both selected, ready to cast vote
    return 'Share Opinion';
  };

  const label = getLabel();
  
  return (
    <div className="position-section">
      {label && <span className="position-label">{label}</span>}
      <div className="position-stats">
        {getStatsBar()}
      </div>
    </div>
  );
};
